import { useEffect, useState } from "react";

function parseHreflang(code) {
  const parts = code.split("-");
  return {
    lang: parts[0] ? parts[0].toLowerCase() : undefined,
    region: parts[1] ? parts[1].toUpperCase() : undefined,
  };
}

function readAlternates() {
  const links = document.querySelectorAll('link[rel="alternate"][hreflang]');
  const parsed = [];
  links.forEach((link) => {
    const hreflang = link.getAttribute("hreflang");
    const href = link.getAttribute("href");
    if (!hreflang || !href) return;
    const { lang, region } = parseHreflang(hreflang);
    parsed.push({ hreflang, lang, region, href });
  });
  return parsed;
}

/**
 * Reads <link rel="alternate" hreflang="xx-YY" href="..."> tags from the
 * page's <head> and returns them as an array of { hreflang, lang, region,
 * href }.
 *
 * `region` (the part after the hyphen, e.g. "CA" in "fr-CA") is the ISO
 * 3166-1 alpha-2 country code — the same format the geo-detect edge
 * function's cookie uses. `lang` is kept too because some countries have
 * more than one entry (e.g. en-CA and fr-CA both being Canada), so callers
 * can prefer the one matching the current page's language.
 *
 * These tags are read live from the DOM rather than passed in as data
 * because libraries like react-helmet write them into <head>
 * asynchronously — often batched into the browser's next animation frame
 * rather than synchronously during React's own commit — so a single read
 * on mount can easily run before the tags actually exist. A
 * MutationObserver on <head> catches them whenever they actually land,
 * regardless of which head-management library or timing your site uses.
 *
 * @param {string} [locationKey] pass something that changes on client-side
 *   navigation (e.g. `${location.pathname}${location.search}`) so the
 *   observer is torn down and re-attached per page — otherwise, because
 *   wrapPageElement keeps this component mounted across route changes, a
 *   single long-lived observer would still work, but re-keying makes the
 *   initial synchronous read (for the new page) happen right away too.
 */
export default function useHreflangAlternates(locationKey) {
  const [alternates, setAlternates] = useState([]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    // Read immediately in case tags are already present (e.g. rendered
    // into the initial server/statically-generated HTML).
    setAlternates(readAlternates());

    // Coalesce bursts of mutations (Helmet typically removes and re-adds
    // several tags at once) into a single re-read rather than thrashing
    // state on every individual DOM change.
    let debounceId = null;
    const observer = new MutationObserver(() => {
      if (debounceId !== null) return;
      debounceId = setTimeout(() => {
        debounceId = null;
        setAlternates(readAlternates());
      }, 0);
    });

    observer.observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["href", "hreflang", "rel"],
    });

    return () => {
      observer.disconnect();
      if (debounceId !== null) clearTimeout(debounceId);
    };
  }, [locationKey]);

  return alternates;
}